import { AsyncPipe, DecimalPipe } from '@angular/common';
import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  WritableSignal,
  computed,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule } from '@angular/forms';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { EmbedPDF } from '@config/embed-pdf';
import { EmbedVideo } from '@config/embed-video';
// Registra i blot anche quando nessun simbolo dei moduli viene usato qui.
import '@config/embed-video-file';
import '@config/embed-galleria';
import { EmbedGalleria, FotoGalleria } from '@config/embed-galleria';
import { mainConfig } from '@config/main.config';
import { join } from '@fireflysemantics/join';
import { Articolo } from '@models/articolo.model';
import { Categoria } from '@models/categoria.model';
import { FileSys } from '@models/fileSystem.model';
import { Provincia } from '@models/provincia.model';
import { Regione } from '@models/regione.model';
import { User } from '@models/user.model';
import { AIService, EventoAI } from '@services/ai.service';
import { ArticoliService } from '@services/articoli.service';
import { AuthService } from '@services/auth.service';
import { CategorieService } from '@services/categorie.service';
import { ProvinceService } from '@services/province.service';
import { RegioniService } from '@services/regioni.service';
import { UploadsService } from '@services/uploads.service';
import { UsersService } from '@services/users.service';
import { UtilsService } from '@services/utils.service';
import { StatoCaricamento, VideoService } from '@services/video.service';
import { PageRequest } from '@sinapsys/ngx-crud';
import { ConfermaService } from '@shared/conferma/conferma-dialog.component';
import { etichettaAvanzamento } from '@shared/utils/avanzamento-generazione.util';
import { etichettaVideo } from '@shared/utils/avanzamento-video.util';
import { messaggioErroreFormattazione } from '@shared/utils/errore-formattazione.util';
import { ErroreFormattazione, formattaConTentativi } from '@shared/utils/formattazione.util';
import { StatoArticolo, statoArticolo } from '@shared/utils/stato-articolo.util';
import { preparaCopertina } from '@shared/utils/prepara-copertina.util';
import { testoSenzaAiutiEditor, BloccoEditor, indiceDopoRimozione, indiceRilascio } from '@shared/utils/trascinamento-video.util';
import { matcherTabelleIncollate } from '@shared/utils/tabelle-incollate.util';
import moment from 'moment';
import { QuillEditorComponent, QuillModule } from 'ngx-quill';
import Quill from 'quill';
import Delta from 'quill-delta';
import { Observable, Subscription, debounceTime, firstValueFrom, of, switchMap } from 'rxjs';
import { GalleriaDialogComponent } from './galleria-dialog.component';
import { PageComponent } from '../../../layout/page/page.component';
import { COMMON_APP_CONFIG } from 'src/app/core/tokens/common-config.token';

/**
 * Avanzamento di una scrittura in corso, mostrata sul pulsante. Finche' il
 * modello ragiona non c'e' testo da mostrare, e senza questo il pannello
 * sembrerebbe fermo per un minuto.
 */
interface StatoGenerazione {
  caratteriScritti: number;
  sta: 'ragionando' | 'scrivendo';
}

/** Quanto spesso la bozza si salva da sola, in millisecondi. */
const INTERVALLO_BOZZA = 45_000;

/**
 * I passi della barra in fondo, nell'ordine in cui si compiono.
 *
 * La guida illumina il primo che ha ancora senso. Non e' un binario: gli altri
 * pulsanti restano premibili, chi sa cosa fa non deve passare di qui.
 */
export type PassoGuida = 'salva' | 'pubblica' | 'cache' | 'social' | 'sito' | 'articoli' | null;

/**
 * I passi gia' compiuti, per articolo, finche' si resta nel pannello.
 *
 * Aggiornare la cache e guardare la pagina non lasciano traccia sul server:
 * l'unico posto dove si puo' ricordare che sono stati fatti e' qui. Una mappa
 * di modulo e non un campo del componente, cosi' andare all'elenco e tornare
 * sull'articolo non azzera il percorso. Si perde al ricaricamento della
 * pagina, ed e' giusto: e' una comodita', non un dato.
 */
const passiCompiuti = new Map<number, Set<Exclude<PassoGuida, null>>>();

/**
 * Quanto puo' essere lungo un titolo.
 *
 * Il limite era 499, la larghezza della colonna: un numero tecnico, non
 * editoriale. Un titolo cosi' lungo nessuno lo scrive, e prima di arrivarci
 * viene tagliato ovunque conti — nei risultati di ricerca, nelle anteprime
 * social, nelle schede della home. 110 e' il conto che si tiene.
 */
export const MASSIMO_TITOLO = 110;

/** Una voce della lista di controllo per la pubblicazione. */
export interface RequisitoPubblicazione {
  campo: string;
  etichetta: string;
  pronto: boolean;
}

/**
 * Editor di un articolo, a pagina intera.
 *
 * Prima viveva in una `mat-dialog` aperta dall'elenco: l'indirizzo non
 * cambiava (niente link condivisibile, niente ricarica), lo spazio era quello
 * che restava dentro la finestra, e su tablet il lavoro principale della
 * redazione si faceva in un riquadro.
 */
@Component({
  selector: 'app-articolo',
  standalone: true,
  imports: [PageComponent, MatProgressBarModule, mainConfig.material, mainConfig.formModules, AsyncPipe, DecimalPipe, FormsModule, QuillModule],
  templateUrl: './articolo.component.html',
  styleUrls: ['./articolo.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ArticoloComponent implements OnInit, OnDestroy {
  @ViewChild('quillEditor') quillEditor!: QuillEditorComponent;

  editItemForm: FormGroup;
  editItem?: Articolo;
  testo: string = '';
  editorModules: any;

  autoriDetailDataSource!: Observable<User[]>;
  categorieDetailDataSource!: Observable<Categoria[]>;
  regioniDataSource: Regione[] = [];
  provinceDataSource: Provincia[] = [];
  provinceFilteredDataSource: Provincia[] = [];

  formattazioneInCorso = signal(false);
  /** La generazione delle keywords e' in corso: il pulsante lo dice e si spegne. */
  readonly keywordsInCorso = signal(false);
  /** Tentativo in corso: il modello ogni tanto sbaglia la copia e si ripete. */
  tentativoFormattazione = signal(1);
  /** Caricamento di un video in corso, con la fase da mostrare sul pulsante. */
  videoInCorso = signal<StatoCaricamento | null>(null);
  /** Video che si sta trascinando dentro l'editor. */
  private videoTrascinato: { blot: any; indice: number } | null = null;
  private editorTrascinamento?: Quill;
  generazioneInCorso = signal<StatoGenerazione | null>(null);
  chatInCorso = signal<StatoGenerazione | null>(null);
  /** Streaming aperto, da chiudere se si lascia la pagina. */
  private streamAperto: Subscription | null = null;

  embedVideo: any = EmbedVideo;
  embedPDF: any = EmbedPDF;

  // --- Stato della pagina ---------------------------------------------------

  readonly caricamento = signal(true);
  /** L'editor si disegna solo a ruolo noto: vedi ngOnInit. */
  readonly editorPronto = signal(false);
  readonly salvataggioInCorso = signal(false);
  /** Momento dell'ultimo salvataggio andato a buon fine. */
  readonly salvatoAlle = signal<Date | null>(null);
  /** Ci sono modifiche non ancora salvate. */
  readonly daSalvare = signal(false);
  /** Titolo gia' usato da un altro articolo: si scopre uscendo dal campo. */
  readonly titoloDuplicato = signal<number | null>(null);

  /** Cambia a ogni passo compiuto: serve solo a far ridisegnare la guida. */
  private readonly passoSegnato = signal(0);

  /** Per il template: il limite sta scritto una volta sola. */
  readonly massimoTitolo = MASSIMO_TITOLO;

  private sottoscrizioni = new Subscription();
  private salvataggioAutomatico?: ReturnType<typeof setInterval>;
  /** Com'era il form all'ultimo caricamento o salvataggio. */
  private istantanea = '';

  constructor(
    public utilsService: UtilsService,
    private articoliService: ArticoliService,
    private categorieService: CategorieService,
    public authService: AuthService,
    private usersService: UsersService,
    private regioniService: RegioniService,
    private provincieService: ProvinceService,
    private snackBar: MatSnackBar,
    private uploadService: UploadsService,
    private aiService: AIService,
    private videoService: VideoService,
    private renderer: Renderer2,
    private router: Router,
    private route: ActivatedRoute,
    private conferma: ConfermaService,
    @Inject(COMMON_APP_CONFIG) private mainConfig: Required<{ api: { apiBaseUrl: string } }>,
    private dialog: MatDialog
  ) {
    this.editItemForm = new FormGroup({
      id: new FormControl<number | undefined>(undefined),
      regione: new FormControl<Regione | undefined>(undefined),
      provincia: new FormControl<Provincia | undefined>(undefined),
      titolo: new FormControl<string | undefined>(undefined),
      categoria: new FormControl<Categoria | undefined>(undefined),
      testo: new FormControl<string | undefined>(undefined),
      autore: new FormControl<User | undefined>(undefined),
      data: new FormControl<Date | undefined>(undefined),
      approvato: new FormControl<number | undefined>(undefined),
      keywords: new FormControl<string | undefined>(undefined),
      img: new FormControl<string | undefined>(undefined),
      evidenza: new FormControl<number | undefined>(undefined),
      evidenza_regione: new FormControl<number | undefined>(undefined),
      permalink: new FormControl<string | undefined>(undefined),
      fileSystem: new FormControl<FileSys | undefined>(undefined),
      chat_con_editor: new FormControl<string | undefined>(undefined),
    });
    this.editorModules = this.costruisciModuliEditor();
  }

  /**
   * Configurazione dell'editor.
   *
   * Sta in un metodo perche' va rifatta quando l'utente e' noto: la toolbar
   * viene costruita da Quill una volta sola, e i comandi dipendono dal ruolo.
   */
  private costruisciModuliEditor(): any {
    return {
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          ['link', 'image', 'video', 'blockquote', 'code-block'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ align: [] }],
          ['clean'],
          this.comandiIncorpora(),
        ],
        handlers: {
          insertEmbed: () => this.showEmbedPopup(),
          uploadVideo: () => this.caricaVideo(),
          insertPDF: () => this.showPDFPopup(),
          galleria: () => this.apriGalleria(),
        },
      },
      // Gli articoli arrivano spesso incollati da ChatGPT, tabelle comprese:
      // senza questi Quill accatasta tutte le intestazioni nella prima cella.
      clipboard: { matchers: matcherTabelleIncollate() },
    };
  }

  // --- Ciclo di vita -------------------------------------------------------

  async ngOnInit(): Promise<void> {
    this.caricaAnagrafiche();
    this.ascoltaModifiche();

    // Si aspetta di sapere chi sta scrivendo: da quello dipendono i comandi
    // della toolbar e l'autore precompilato su un articolo nuovo. Una pagina
    // aperta direttamente dall'indirizzo nasce mentre `me` e' ancora in volo.
    await this.authService.attendiUtente();

    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'nuovo') {
      this.caricaArticolo(Number(id));
    } else {
      this.preparaNuovo();
    }

    this.editorModules = this.costruisciModuliEditor();
    this.editorPronto.set(true);
  }

  ngOnDestroy(): void {
    // Lasciare la pagina interrompe anche la richiesta: senza questo il backend
    // resterebbe a consumare token per un editor che non c'e' piu'.
    this.streamAperto?.unsubscribe();
    this.sottoscrizioni.unsubscribe();
    if (this.salvataggioAutomatico) clearInterval(this.salvataggioAutomatico);
  }

  /** Chiesto dalla guardia sull'uscita dalla pagina. */
  haModificheNonSalvate(): boolean {
    return this.daSalvare();
  }

  private fissaIstantanea(): void {
    this.istantanea = JSON.stringify(this.editItemForm.getRawValue());
    this.daSalvare.set(false);
  }

  private confrontaConIstantanea(): void {
    this.daSalvare.set(JSON.stringify(this.editItemForm.getRawValue()) !== this.istantanea);
  }

  /**
   * L'editor appena creato scrive nel form la propria versione del testo, che
   * puo' differire per dettagli di formattazione da quella salvata: da quel
   * momento vale come punto di partenza.
   */
  editorCreato(editor: any): void {
    this.preparaTrascinamentoVideo(editor);
    this.fissaIstantanea();
  }

  // --- Caricamento ---------------------------------------------------------

  private caricaAnagrafiche(): void {
    this.regioniService.search({}, new PageRequest(0, -1)).subscribe((res) => {
      this.regioniDataSource = res.items.map((r: any) => new Regione(r));
      this.regioniDataSource.sort((a, b) => a.nomeregione.localeCompare(b.nomeregione));
    });

    this.provincieService.search({ $with: ['regione'] }, new PageRequest(0, -1)).subscribe((res) => {
      this.provinceDataSource = res.items.map((r: any) => new Provincia(r));
      this.filtraProvince(this.editItemForm.controls['regione'].value);
    });

    this.autoriDetailDataSource = this.editItemForm.controls['autore'].valueChanges.pipe(
      debounceTime(350),
      switchMap((cercato) =>
        typeof cercato === 'string' && cercato.trim() ? this.usersService.getAutocomplete(cercato) : of([])
      )
    );

    this.categorieDetailDataSource = this.editItemForm.controls['categoria'].valueChanges.pipe(
      debounceTime(350),
      switchMap((cercato) =>
        typeof cercato === 'string' && cercato.trim() ? this.categorieService.getAutocomplete(cercato) : of([])
      )
    );
  }

  private preparaNuovo(): void {
    const articolo = new Articolo();
    articolo.data = moment().unix();
    articolo.evidenza = '0';
    articolo.evidenza_regione = '0';
    articolo.approvato = '0';
    articolo.autore = this.authService.user as User;

    this.editItemForm.patchValue({
      data: moment().format('YYYY-MM-DDTHH:mm'),
      evidenza: '0',
      evidenza_regione: '0',
      approvato: '0',
      autore: articolo.autore ? new User({ id: articolo.autore.id, blogger: articolo.autore.blogger }) : null,
    }, { emitEvent: false });
    // Il permalink si compone dal titolo mentre si scrive.
    this.editItemForm.controls['permalink'].disable({ emitEvent: false });

    this.editItem = articolo;
    this.caricamento.set(false);
    this.fissaIstantanea();
  }

  private caricaArticolo(id: number): void {
    this.caricamento.set(true);
    this.editItemForm.controls['permalink'].disable({ emitEvent: false });

    this.articoliService.search({ id, $with: ['regione', 'provincia', 'fileSystem'] }, new PageRequest(0, 1)).subscribe({
      next: (res) => {
        const trovato = res.items[0] as Articolo | undefined;
        if (!trovato) {
          this.utilsService.notificationsService.showNotification('', 'Articolo non trovato', 'error');
          this.router.navigateByUrl('/gestione-articoli/articoli');
          return;
        }
        this.riempiForm(new Articolo(trovato));
        this.caricamento.set(false);
      },
      error: (err) => {
        this.caricamento.set(false);
        this.utilsService.defaultHttpErrorHandler(err);
      },
    });
  }

  private riempiForm(articolo: Articolo): void {
    this.editItem = articolo;

    const zitto = { emitEvent: false } as const;
    // La data si scrive dopo, nel formato del campo: passandola qui il campo
    // riceverebbe il timestamp grezzo e il browser se ne lamenterebbe.
    const { data, ...senzaData } = articolo as unknown as Record<string, unknown>;
    this.editItemForm.patchValue(senzaData, zitto);
    this.testo = articolo.testo;
    // `id: 0` e' il modo in cui questo backend dice "nessuno": va letto come
    // assente, non come un riferimento valido. Col vecchio controllo su
    // `!= undefined` un campo svuotato tornava come oggetto con id 0, che la
    // lista di controllo per la pubblicazione contava come compilato.
    const scelto = (relazione: { id?: number } | undefined | null) => !!relazione && Number(relazione.id) > 0;

    this.editItemForm.controls['autore'].setValue(
      scelto(articolo.autore) ? new User({ id: articolo.autore.id, blogger: articolo.autore.blogger }) : null,
      zitto
    );
    this.editItemForm.controls['categoria'].setValue(
      scelto(articolo.categoria) ? new Categoria({ id: articolo.categoria.id, nome: articolo.categoria.nome }) : null,
      zitto
    );
    this.editItemForm.controls['regione'].setValue(
      scelto(articolo.regione) ? new Regione({ id: articolo.regione.id, nomeregione: articolo.regione.nomeregione }) : null,
      zitto
    );
    this.editItemForm.controls['provincia'].setValue(
      scelto(articolo.provincia)
        ? new Provincia({ id: articolo.provincia.id, nomeprovincia: articolo.provincia.nomeprovincia })
        : null,
      zitto
    );
    this.editItemForm.controls['fileSystem'].setValue(scelto(articolo.fileSystem) ? new FileSys(articolo.fileSystem) : null, zitto);
    if (articolo.data) {
      this.editItemForm.controls['data'].setValue(moment.unix(articolo.data).format('YYYY-MM-DDTHH:mm'), zitto);
    }

    this.filtraProvince(this.editItemForm.controls['regione'].value);
    this.mostraCopertina(articolo.fileSystem);
    this.fissaIstantanea();
  }

  /**
   * Anteprima della copertina già caricata.
   *
   * Prima l'immagine veniva riscaricata e convertita in base64 solo per
   * ricostruire un oggetto `File` da mostrare: per una copertina da 1200px
   * significa trasportare mezzo megabyte per far vedere una miniatura. Basta
   * l'indirizzo.
   */
  private mostraCopertina(file: FileSys | undefined): void {
    if (!file?.disk_name) {
      this.imagePreview.set('');
      this.imageName.set('');
      this.fileSize.set(0);
      return;
    }
    this.imagePreview.set(this.utilsService.urlImmagine(file.disk_name));
    this.imageName.set(file.file_name);
    this.fileSize.set(file.file_size);
    this.selectedFile = null;
    // Delle dimensioni di una copertina già in archivio non sappiamo nulla
    // finché il browser non la carica: ci pensa il controllo sull'anteprima.
    this.avvisoImmagine.set(null);
    this.immagineConforme.set(false);
  }

  private filtraProvince(regione: Regione | null | undefined): void {
    this.provinceFilteredDataSource = regione?.id
      ? this.provinceDataSource.filter((p) => p.regione?.id === regione.id)
      : this.provinceDataSource;
  }

  private ascoltaModifiche(): void {
    this.sottoscrizioni.add(this.editItemForm.valueChanges.subscribe(() => this.confrontaConIstantanea()));

    // Salvataggio automatico della bozza: un testo generato dal modello vive
    // solo nel form, e una sessione scaduta se lo porta via.
    this.salvataggioAutomatico = setInterval(() => this.salvaBozzaSePossibile(), INTERVALLO_BOZZA);
  }

  // --- Lista di controllo per la pubblicazione -----------------------------

  /**
   * Cosa serve prima di mandare online.
   *
   * Prima questi undici controlli stavano dentro `save()` e producevano un
   * unico messaggio, "Per pubblicare un articolo e' necessario compilare tutti
   * i campi", senza dire quale mancasse, con i campi sparsi lungo una finestra
   * piu' alta dello schermo.
   */
  requisiti(): RequisitoPubblicazione[] {
    const v = this.editItemForm.getRawValue();
    const pieno = (t: unknown) => typeof t === 'string' && t.trim().length > 0;

    return [
      { campo: 'titolo', etichetta: 'Titolo', pronto: pieno(v.titolo) },
      // Un titolo oltre il limite non e' un campo mancante ma un campo
      // sbagliato, e blocca comunque la pubblicazione: viene tagliato in ogni
      // posto dove conta, e tagliato male.
      { campo: 'titolo', etichetta: `Titolo entro ${MASSIMO_TITOLO} battute`, pronto: !this.titoloTroppoLungo() },
      { campo: 'permalink', etichetta: 'Permalink', pronto: pieno(v.permalink) },
      { campo: 'testo', etichetta: "Testo dell'articolo", pronto: pieno(v.testo) },
      { campo: 'copertina', etichetta: 'Immagine di copertina', pronto: !!v.fileSystem?.id },
      { campo: 'categoria', etichetta: 'Categoria', pronto: !!v.categoria?.id },
      { campo: 'autore', etichetta: 'Autore', pronto: !!v.autore?.id },
      { campo: 'data', etichetta: 'Data di pubblicazione', pronto: !!v.data },
      { campo: 'regione', etichetta: 'Regione', pronto: !!v.regione?.id },
      { campo: 'provincia', etichetta: 'Provincia', pronto: !!v.provincia?.id },
      { campo: 'keywords', etichetta: 'Keywords', pronto: pieno(v.keywords) },
    ];
  }

  mancanti(): RequisitoPubblicazione[] {
    return this.requisiti().filter((r) => !r.pronto);
  }

  /** Quante battute ha il titolo adesso. */
  lunghezzaTitolo(): number {
    return String(this.editItemForm.controls['titolo'].value ?? '').trim().length;
  }

  /**
   * Il titolo supera il limite.
   *
   * Il campo non ha piu' `maxlength`: smetteva di accettare caratteri senza
   * dire niente, e chi scriveva non si accorgeva che il titolo era stato
   * troncato. Meglio lasciarlo scrivere, dirglielo, e non lasciarlo pubblicare
   * finche' non rientra.
   */
  titoloTroppoLungo(): boolean {
    return this.lunghezzaTitolo() > MASSIMO_TITOLO;
  }

  puoPubblicare(): boolean {
    return this.mancanti().length === 0 && !!this.editItemForm.controls['id'].value;
  }

  /**
   * L'articolo e' davvero online: non una bozza, non programmato.
   *
   * Conta lo stato salvato sul server, non quello del form: un "Pubblica" il
   * cui salvataggio fallisce lascia `approvato = '1'` nel form, mentre la
   * pagina pubblica di una bozza risponde 404.
   */
  articoloOnline(): boolean {
    if (!this.editItem) return false;
    return statoArticolo({ approvato: this.editItem.approvato, data: this.editItem.data }).codice === 'pubblicato';
  }

  /** La condivisione sui social e' gia' stata fatta: lo registra il server. */
  condiviso(): boolean {
    return this.editItem?.shared == 1;
  }

  /**
   * Le azioni sulla pagina pubblica si mostrano solo quando una pagina
   * pubblica c'e'.
   *
   * Prima comparivano appena l'articolo aveva un id, cioe' subito dopo il
   * primo salvataggio: su una bozza offriva di rigenerare la cache di una
   * pagina che non esiste.
   */
  mostraAzioniSito(): boolean {
    return !!this.editItemForm.controls['id'].value && this.articoloOnline();
  }

  /**
   * "Visualizza sul sito" viene per ultimo: solo a pubblicazione fatta e
   * condivisione completata.
   *
   * Non e' solo ordine. Aprire la pagina prima della condivisione significa
   * scaldare la cache con una versione che i social non hanno ancora letto, e
   * l'anteprima che si portano dietro e' quella che trovano al primo passaggio.
   */
  mostraVisualizzaSulSito(): boolean {
    return this.mostraAzioniSito() && this.condiviso();
  }

  // --- La guida in fondo ----------------------------------------------------

  /** Segna un passo come compiuto per questo articolo. */
  private segnaPasso(passo: Exclude<PassoGuida, null>): void {
    const id = Number(this.editItemForm.controls['id'].value);
    if (!id) return;
    if (!passiCompiuti.has(id)) passiCompiuti.set(id, new Set());
    passiCompiuti.get(id)!.add(passo);
    // I segnali sono gia' cambiati altrove; questo serve a far ridisegnare la
    // guida anche quando l'unica cosa cambiata e' il passo.
    this.passoSegnato.update((n) => n + 1);
  }

  private passoFatto(passo: Exclude<PassoGuida, null>): boolean {
    const id = Number(this.editItemForm.controls['id'].value);
    return !!id && !!passiCompiuti.get(id)?.has(passo);
  }

  /**
   * Il passo da fare adesso, letto dallo stato dell'articolo.
   *
   * Non c'e' un contatore che avanza: se ci fosse, riaprendo un articolo gia'
   * online la guida direbbe di pubblicarlo. Qui invece ogni apertura riparte
   * da dove l'articolo e' arrivato davvero.
   *
   * Torna `null` quando non c'e' un passo sensato da indicare: su una bozza a
   * cui mancano ancora dei campi la guida tacerebbe puntando "Pubblica", che e'
   * spento — a dire cosa manca c'e' gia' la lista a fianco.
   */
  passoCorrente(): PassoGuida {
    this.passoSegnato();  // dipendenza esplicita, per ridisegnare

    if (this.salvataggioInCorso()) return null;
    if (!this.editItemForm.controls['id'].value) return 'salva';
    if (this.daSalvare()) return 'salva';

    const stato = this.stato().codice;
    if (stato === 'bozza') return this.puoPubblicare() ? 'pubblica' : null;
    // Programmato: online non c'e' ancora, non c'e' cache da rigenerare ne'
    // niente da condividere. Si aspetta.
    if (stato === 'programmato') return null;

    if (!this.passoFatto('cache')) return 'cache';
    if (!this.condiviso()) return 'social';
    if (!this.passoFatto('sito')) return 'sito';
    return 'articoli';
  }

  /** Per il template: questo pulsante e' quello da premere adesso? */
  guidato(passo: Exclude<PassoGuida, null>): boolean {
    return this.passoCorrente() === passo;
  }

  /** Porta il cursore sul campo che manca, invece di farlo cercare. */
  vaiAlCampo(campo: string): void {
    const nodo = document.getElementById(`campo-${campo}`);
    if (!nodo) return;
    nodo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    (nodo.querySelector('input, textarea, select') as HTMLElement | null)?.focus();
  }

  stato(): StatoArticolo {
    return statoArticolo({
      approvato: this.editItemForm.controls['approvato'].value,
      data: this.editItem?.data,
    });
  }

  nomeCategoria(categoria: Categoria | string | null): string {
    return typeof categoria === 'object' && categoria ? categoria.nome : (categoria ?? '');
  }

  nomeAutore(autore: User | string | null): string {
    return typeof autore === 'object' && autore ? autore.blogger : (autore ?? '');
  }

  titoloPagina(): string {
    return this.editItemForm.controls['id'].value ? 'Modifica articolo' : 'Nuovo articolo';
  }

  // --- Titolo doppio -------------------------------------------------------

  /**
   * Il controllo sul titolo già usato stava dentro `save()`: lo si scopriva
   * dopo aver scritto tutto l'articolo. Qui parte uscendo dal campo.
   */
  controllaTitoloDuplicato(): void {
    const titolo = this.editItemForm.controls['titolo'].value?.trim();
    const mioId = this.editItemForm.controls['id'].value;
    if (!titolo) {
      this.titoloDuplicato.set(null);
      return;
    }

    this.articoliService.search({ titolo }, new PageRequest(0, 2)).subscribe({
      next: (res) => {
        const altro = res.items.find((a) => a.id !== mioId);
        this.titoloDuplicato.set(altro ? altro.id : null);
      },
      error: () => this.titoloDuplicato.set(null),
    });
  }

  // --- Salvataggio ---------------------------------------------------------

  /** Salva senza toccare lo stato di pubblicazione. */
  async salvaBozza(): Promise<void> {
    await this.salva();
  }

  /**
   * Manda online.
   *
   * La guardia ripete la condizione del pulsante, e non e' ridondante: il
   * pulsante e' `disabledInteractive`, perche' altrimenti il tooltip che
   * spiega cosa manca non si vedrebbe proprio quando serve. Material in quel
   * caso toglie l'attributo `disabled` e lascia passare il click — il blocco
   * degli eventi ce l'hanno solo gli `<a mat-button>`, non i `<button>`.
   *
   * La data diventa l'ora di adesso, se non e' nel futuro. Il campo si riempie
   * all'apertura dell'editor, e il sito ordina per data: un pezzo scritto in
   * venti minuti usciva datato all'inizio del lavoro, sotto articoli
   * pubblicati prima di lui. Una data futura invece resta: e' una
   * pubblicazione programmata.
   */
  async pubblica(): Promise<void> {
    if (!this.puoPubblicare() || this.salvataggioInCorso() || this.stato().codice !== 'bozza') return;
    const data = this.editItemForm.controls['data'];
    const adesso = moment();
    if (!moment(data.value, 'YYYY-MM-DDTHH:mm').isAfter(adesso)) {
      // Al minuto, per difetto: arrotondata in su sarebbe nel futuro, cioe' programmata.
      data.setValue(adesso.format('YYYY-MM-DDTHH:mm'));
    }
    this.editItemForm.controls['approvato'].setValue('1');
    await this.salva();
  }

  /** Riporta l'articolo in bozza: smette di essere visibile sul sito. */
  async riportaInBozza(): Promise<void> {
    const procedi = await this.conferma.chiedi({
      titolo: "Togliere l'articolo dal sito?",
      testo: 'Torna in bozza e non sarà più raggiungibile dai lettori.',
      conferma: 'Riporta in bozza',
    });
    if (!procedi) return;
    this.editItemForm.controls['approvato'].setValue('0');
    await this.salva();
  }

  private async salvaBozzaSePossibile(): Promise<void> {
    if (!this.daSalvare() || this.salvataggioInCorso()) return;
    if (!this.editItemForm.controls['id'].value) return; // un articolo nuovo lo salva la persona
    if (this.generazioneInCorso() || this.chatInCorso() || this.formattazioneInCorso()) return;
    await this.salva({ automatico: true });
  }

  /**
   * Salva l'articolo.
   *
   * Con `automatico` passa dalla rotta della bozza, che tocca solo titolo,
   * testo e keywords: un salvataggio che parte da solo non deve cambiare lo
   * stato di pubblicazione.
   */
  private async salva(opzioni: { automatico?: boolean } = {}): Promise<void> {
    if (this.salvataggioInCorso()) return;

    const raw = this.editItemForm.getRawValue();
    delete raw.chat_con_editor;

    if (opzioni.automatico) {
      this.salvataggioInCorso.set(true);
      this.articoliService.salvaBozza(raw.id, { titolo: raw.titolo, testo: raw.testo, keywords: raw.keywords }).subscribe({
        next: () => {
          this.salvataggioInCorso.set(false);
          this.salvatoAlle.set(new Date());
          this.fissaIstantanea();
        },
        error: () => {
          // Un salvataggio automatico che fallisce non interrompe il lavoro:
          // resta scritto che ci sono modifiche da salvare.
          this.salvataggioInCorso.set(false);
        },
      });
      return;
    }

    if (raw.data) raw.data = moment(raw.data, 'YYYY-MM-DDTHH:mm').unix();
    else raw.data = moment().unix();

    // Le relazioni per intero, prima di appiattirle a soli id qui sotto.
    //
    // Servono dopo: il salvataggio manda `{ id }` e il backend risponde con
    // gli stessi moncherini, senza `nome`, `blogger` o `disk_name`. Ricostruire
    // il form da quella risposta svuotava a video Categoria, Redattore e
    // l'anteprima della copertina a ogni salvataggio, mentre in archivio i dati
    // restavano — per questo online si continuavano a vedere.
    const relazioni = this.relazioniDaTenere(raw);

    if (raw.autore?.id) raw.autore = { id: raw.autore.id };
    else raw.autore = { id: 0 };

    if (raw.categoria?.id) raw.categoria = { id: raw.categoria.id };
    else raw.categoria = { id: 0 };

    if (raw.regione?.id) raw.regione = { id: raw.regione.id };
    else delete raw.regione;

    if (raw.provincia?.id) raw.provincia = { id: raw.provincia.id };
    else delete raw.provincia;

    if (raw.fileSystem?.id) raw.fileSystem = { id: raw.fileSystem.id };
    else delete raw.fileSystem;

    if (raw.evidenza == null) raw.evidenza = '0';
    if (raw.evidenza_regione == null) raw.evidenza_regione = '0';

    const eraNuovo = !raw.id;
    this.salvataggioInCorso.set(true);

    this.articoliService.save(JSON.parse(JSON.stringify(raw))).subscribe({
      next: (res) => {
        this.salvataggioInCorso.set(false);
        this.salvatoAlle.set(new Date());
        this.utilsService.notificationsService.showNotification('', 'Articolo salvato', 'success');

        // Si resta dove si era. Prima il salvataggio chiudeva la finestra e la
        // riapriva: l'editor si ricostruiva da zero e si perdevano punto di
        // scrittura e scorrimento.
        //
        // Alla risposta si rimettono le relazioni intere: quello che e' appena
        // stato salvato e' proprio cio' che c'era nel form, e il backend non lo
        // rimanda per esteso.
        this.riempiForm(new Articolo({ ...res, ...relazioni }));

        if (eraNuovo && this.editItem?.id) {
          // Da qui l'articolo ha un indirizzo suo, e il permalink si smette di
          // ricalcolare dal titolo.
          this.router.navigate(['/gestione-articoli/articoli', this.editItem.id], { replaceUrl: true });
        }
      },
      error: (err) => {
        this.salvataggioInCorso.set(false);
        this.utilsService.defaultHttpErrorHandler(err);
      },
    });
  }

  async elimina(): Promise<void> {
    if (!this.authService.user?.can('owners')) return;

    const procedi = await this.conferma.chiedi({
      titolo: 'Eliminare questo articolo?',
      testo: `"${this.editItemForm.controls['titolo'].value ?? ''}" verrà rimosso definitivamente. L’operazione non si può annullare.`,
      conferma: 'Elimina',
      distruttiva: true,
    });
    if (!procedi) return;

    this.articoliService.delete(this.editItemForm.controls['id'].value).subscribe({
      next: () => {
        this.daSalvare.set(false);
        this.utilsService.notificationsService.showNotification('', 'Articolo eliminato', 'success');
        this.router.navigateByUrl('/gestione-articoli/articoli');
      },
      error: (err) => this.utilsService.defaultHttpErrorHandler(err),
    });
  }

  /**
   * Le relazioni del form che vanno rimesse nella risposta del salvataggio.
   *
   * Solo quelle davvero scelte: un campo svuotato deve restare svuotato, e
   * `{ id: 0 }` e' il modo in cui questo backend dice "nessuno", non un
   * riferimento valido.
   */
  private relazioniDaTenere(raw: any): Record<string, unknown> {
    const tenute: Record<string, unknown> = {};
    for (const campo of ['autore', 'categoria', 'regione', 'provincia', 'fileSystem']) {
      const valore = raw[campo];
      if (valore && typeof valore === 'object' && Number(valore.id) > 0) tenute[campo] = valore;
    }
    return tenute;
  }

  tornaAllElenco(): void {
    this.router.navigateByUrl('/gestione-articoli/articoli');
  }

  onChangeRegione(event: any) {
    this.filtraProvince(event.value);
    // La provincia scelta prima puo' non appartenere alla regione nuova.
    const provincia = this.editItemForm.controls['provincia'].value;
    if (provincia && !this.provinceFilteredDataSource.some((p) => p.id === provincia.id)) {
      this.editItemForm.controls['provincia'].setValue(null);
    }
  }

  onInputTitle(event: any) {
    if (this.editItemForm.controls['id'].value != null) return;
    const permalink = this.utilsService.generatePermalink(event.target.value);
    this.editItemForm.controls['permalink'].setValue(permalink);
  }


  // --- Fotogallery ----------------------------------------------------------

  /**
   * Apre la finestra della gallery.
   *
   * Se il cursore e' su una gallery che c'e' gia', quella si modifica: la
   * finestra parte dalle foto esistenti e il risultato prende il posto della
   * vecchia in un solo aggiornamento, cosi' l'Annulla dell'editor torna
   * indietro di un passo e non di due.
   */
  async apriGalleria(): Promise<void> {
    const editor = this.quillEditor?.quillEditor;
    if (!editor) return;

    const selezione = editor.getSelection(true);
    const indice = selezione?.index ?? editor.getLength();
    const esistente = this.galleriaAllIndice(editor, indice);

    const foto = await firstValueFrom(
      this.dialog
        .open<GalleriaDialogComponent, FotoGalleria[] | null, FotoGalleria[] | undefined>(GalleriaDialogComponent, {
          data: esistente ? esistente.foto : null,
          width: '760px',
          maxWidth: '95vw',
          autoFocus: false,
        })
        .afterClosed()
    );

    // `undefined` vuol dire annullato; un elenco vuoto vuol dire "toglila".
    if (foto === undefined) return;

    const modifica = new Delta();
    if (esistente) {
      modifica.retain(esistente.indice).delete(1);
      if (foto.length) modifica.insert({ galleria: foto });
    } else if (foto.length) {
      modifica.retain(indice).insert({ galleria: foto });
    } else {
      return;
    }

    editor.history.cutoff();
    editor.updateContents(modifica, 'user');
    editor.history.cutoff();
    this.editItemForm.controls['testo'].setValue(testoSenzaAiutiEditor(editor.root.innerHTML));
  }

  /** La gallery su cui sta il cursore, se ce n'e' una. */
  private galleriaAllIndice(editor: Quill, indice: number): { indice: number; foto: FotoGalleria[] } | null {
    // Il cursore su un blocco incorporato puo' trovarsi sia prima sia dopo:
    // si guardano entrambe le posizioni.
    for (const posizione of [indice, Math.max(0, indice - 1)]) {
      const [blot] = (editor.scroll as any).descendant(EmbedGalleria, posizione) as [any, number];
      if (blot) {
        return { indice: editor.getIndex(blot), foto: EmbedGalleria.value(blot.domNode) };
      }
    }
    return null;
  }

  // --- Azioni sull'articolo -------------------------------------------------

  apriSulSito(aggiornaCache = false): void {
    const permalink = this.editItemForm.controls['permalink'].value;
    const id = this.editItemForm.controls['id'].value;
    if (!id || !this.articoloOnline()) return;
    const indirizzo = `https://www.infooggi.it/articolo/${permalink}/${id}`;
    window.open(aggiornaCache ? `${indirizzo}/f` : indirizzo, '_blank');
    // Il server non sa che l'abbiamo fatto: se lo ricorda la guida, per non
    // continuare a indicare un passo gia' compiuto.
    this.segnaPasso(aggiornaCache ? 'cache' : 'sito');
  }

  pubblicaSuiSocial(): void {
    const permalink = this.editItemForm.controls['permalink'].value;
    const id = this.editItemForm.controls['id'].value;
    if (!id) return;
    // Su una bozza o su un articolo programmato la pagina pubblica non
    // risponde 200 e il backend rifiuta con un 409: i social salverebbero
    // un'anteprima con "Pagina non trovata", e la salvano per sempre. Anche
    // qui la guardia serve perche' il pulsante e' `disabledInteractive`.
    if (this.stato().codice !== 'pubblicato') return;

    this.aiService.socialPublish(`https://www.infooggi.it/articolo/${permalink}/${id}`, String(id)).subscribe({
      next: (res) => {
        this.utilsService.notificationsService.showNotification('', 'Articolo condiviso con successo!', 'success');
        // La condivisione la registra il server con `shared`, quindi qui basta
        // rileggere la risposta: la guida passa al passo dopo da sola.
        // `socialPublish` e' dichiarato `string` ma risponde l'articolo: si
        // passa dall'oggetto gia' costruito invece di forzare il tipo.
        const aggiornato = new Articolo(res);
        this.riempiForm(new Articolo({ ...aggiornato, ...this.relazioniDaTenere(this.editItemForm.getRawValue()) }));
      },
      error: (err) => {
        // Il backend risponde 409 quando la pagina pubblica non risponde 200:
        // in quel caso i social memorizzerebbero un'anteprima con "Pagina non
        // trovata".
        const messaggio =
          err?.error?.messaggio || "Condivisione non riuscita. Controlla che l'articolo sia raggiungibile sul sito e riprova.";
        this.utilsService.notificationsService.showNotification('', messaggio, 'error');
      },
    });
  }

  /** Testo dello stato del salvataggio, in fondo alla pagina. */
  statoSalvataggio(): string {
    if (this.salvataggioInCorso()) return 'Salvataggio…';
    if (this.daSalvare()) return 'Modifiche non salvate';
    const quando = this.salvatoAlle();
    if (!quando) return '';
    const due = (n: number) => String(n).padStart(2, '0');
    return `Salvato alle ${due(quando.getHours())}:${due(quando.getMinutes())}`;
  }

  private comandiIncorpora(): Record<string, string>[] {
    const comandi: Record<string, string>[] = [{ insertEmbed: 'insertEmbed' }];
    if (this.puoCaricareVideo()) comandi.push({ uploadVideo: 'uploadVideo' });
    comandi.push({ insertPDF: 'insertPDF' }, { galleria: 'galleria' });
    return comandi;
  }

  puoCaricareVideo(): boolean {
    return this.authService?.user?.can('owners') === true;
  }

  async showEmbedPopup(): Promise<void> {
    const embed = await this.utilsService.prompt(
      'Incorpora un video',
      "Incolla qui il codice di incorporamento (l'iframe) del video"
    );
    if (embed) {
      const range = this.quillEditor.quillEditor.getSelection(true);
      const embedValue = this.utilsService.parseIframeAttributes(embed);
      if (!embedValue?.src) {
        this.utilsService.notificationsService.showNotification('', 'Codice embed video non valido', 'error');
        return;
      }
      const index = range?.index ?? this.quillEditor.quillEditor.getLength();
      this.quillEditor.quillEditor.insertEmbed(index, 'embed-video', embedValue);
      this.quillEditor.quillEditor.insertText(index + 1, '\n');
      this.editItemForm.controls['testo'].setValue(testoSenzaAiutiEditor(this.quillEditor.quillEditor.root.innerHTML));
    }
  }

  /**
   * Rende trascinabili i video gia' presenti nel testo.
   *
   * Gli attributi non stanno nel markup salvato: servono solo mentre si
   * scrive, e `contenteditable` dentro l'articolo rende invalida la pagina
   * AMP. Vengono rimessi a ogni cambio di contenuto perche' Quill ricostruisce
   * i nodi quando ricarica il testo.
   */
  preparaVideoTrascinabili(editor = this.quillEditor?.quillEditor): void {
    if (!editor) {
      return;
    }
    editor.root.querySelectorAll('.video-locale-wrapper').forEach((nodo: Element) => {
      // Solo se manca: scrivere un attributo che c'e' gia' e' una mutazione del
      // DOM, Quill la osserva e rilancia il cambio di contenuto, che rilancia
      // questa funzione. Con il controllo, dal secondo giro non succede piu'
      // niente.
      if (nodo.getAttribute('draggable') !== 'true') {
        nodo.setAttribute('draggable', 'true');
      }
      if (nodo.getAttribute('contenteditable') !== 'false') {
        nodo.setAttribute('contenteditable', 'false');
      }
    });
  }

  /**
   * Trascinamento dei video dentro l'editor.
   *
   * Quill gestisce da se' il trascinamento del testo, quindi qui si intercetta
   * l'evento solo quando parte da un video: il resto continua a funzionare
   * come prima.
   */
  preparaTrascinamentoVideo(editor = this.quillEditor?.quillEditor): void {
    if (!editor) {
      return;
    }
    this.aggiornaPulsanteVideo(editor);
    this.preparaVideoTrascinabili(editor);
    if (this.editorTrascinamento === editor) {
      return;
    }
    this.editorTrascinamento = editor;
    const radice: HTMLElement = editor.root;

    radice.addEventListener('dragstart', (evento: DragEvent) => {
      const blocco = (evento.target as HTMLElement)?.closest?.('.video-locale-wrapper');
      if (!blocco) {
        return;
      }
      const blot: any = Quill.find(blocco);
      this.videoTrascinato = blot && typeof blot.length === 'function' ? { blot, indice: editor.getIndex(blot) } : null;
      if (!this.videoTrascinato) {
        return;
      }
      // Quill annulla ogni dragstart: intercetta il video prima del suo gestore.
      evento.stopImmediatePropagation();
      if (evento.dataTransfer) {
        evento.dataTransfer.effectAllowed = 'move';
        // Firefox non avvia il trascinamento se non c'e' niente da trasportare.
        evento.dataTransfer.setData('text/plain', 'video');
      }
    }, { capture: true });

    radice.addEventListener('dragover', (evento: DragEvent) => {
      if (!this.videoTrascinato) {
        return;
      }
      evento.preventDefault();
      if (evento.dataTransfer) {
        evento.dataTransfer.dropEffect = 'move';
      }
      this.mostraSegnoRilascio(evento.clientY);
    });

    radice.addEventListener('dragleave', (evento: DragEvent) => {
      if (evento.target === radice) {
        this.togliSegnoRilascio();
      }
    });

    radice.addEventListener('drop', (evento: DragEvent) => {
      const trascinato = this.videoTrascinato;
      this.togliSegnoRilascio();
      this.videoTrascinato = null;
      if (!trascinato) {
        return;
      }
      evento.preventDefault();
      evento.stopImmediatePropagation();

      const arrivo = indiceRilascio(this.blocchiEditor(), evento.clientY);
      if (arrivo === null || (arrivo >= trascinato.indice && arrivo <= trascinato.indice + 1)) {
        return;
      }

      const valore = trascinato.blot.value?.()?.['embed-video-file'] ?? trascinato.blot.value?.();
      if (!valore) {
        return;
      }

      // Un solo aggiornamento: niente stato intermedio senza video, un solo Annulla.
      const modifica = new Delta();
      if (arrivo < trascinato.indice) {
        modifica.retain(arrivo).insert({ 'embed-video-file': valore }).retain(trascinato.indice - arrivo).delete(1);
      } else {
        modifica.retain(trascinato.indice).delete(1).retain(arrivo - trascinato.indice - 1).insert({ 'embed-video-file': valore });
      }
      editor.history.cutoff();
      editor.updateContents(modifica, 'user');
      editor.history.cutoff();
      editor.setSelection(indiceDopoRimozione(trascinato.indice, arrivo) + 1, 0, 'silent');
      this.preparaVideoTrascinabili();
      this.editItemForm.controls['testo'].setValue(testoSenzaAiutiEditor(editor.root.innerHTML), { emitModelToViewChange: false });
    }, { capture: true });

    radice.addEventListener('dragend', () => {
      this.togliSegnoRilascio();
      this.videoTrascinato = null;
    });
  }

  /** Riquadri dei blocchi dell'editor, con gli indici Quill che li delimitano. */
  private blocchiEditor(): BloccoEditor[] {
    const editor = this.quillEditor.quillEditor;
    const blocchi: BloccoEditor[] = [];
    Array.from(editor.root.children).forEach((figlio: Element) => {
      // Quill.find puo' restituire anche l'editor stesso: qui interessano solo
      // i blot, cioe' i nodi che hanno una posizione e una lunghezza nel testo.
      const blot: any = Quill.find(figlio);
      if (!blot || typeof blot.length !== 'function') {
        return;
      }
      const inizio = editor.getIndex(blot);
      const riquadro = figlio.getBoundingClientRect();
      blocchi.push({ inizio, fine: inizio + blot.length(), top: riquadro.top, bottom: riquadro.bottom });
    });
    return blocchi;
  }

  /** Riga che mostra fra quali due blocchi finira' il video. */
  private mostraSegnoRilascio(y: number): void {
    const blocchi = Array.from(this.quillEditor.quillEditor.root.children) as HTMLElement[];
    blocchi.forEach((b) => b.classList.remove('rilascio-sopra', 'rilascio-sotto'));
    const bersaglio = blocchi.find((b) => y <= b.getBoundingClientRect().bottom) ?? blocchi[blocchi.length - 1];
    if (!bersaglio) {
      return;
    }
    const riquadro = bersaglio.getBoundingClientRect();
    const meta = riquadro.top + riquadro.height / 2;
    bersaglio.classList.add(y < meta ? 'rilascio-sopra' : 'rilascio-sotto');
  }

  private togliSegnoRilascio(): void {
    const editor = this.quillEditor?.quillEditor;
    if (!editor) {
      return;
    }
    Array.from(editor.root.children).forEach((b: Element) => b.classList.remove('rilascio-sopra', 'rilascio-sotto'));
  }

  /** Scritta del pulsante di caricamento video. */
  avanzamentoVideo(stato: StatoCaricamento | null): string {
    return etichettaVideo(stato);
  }

  private aggiornaPulsanteVideo(editor = this.quillEditor?.quillEditor): void {
    const toolbar = editor?.getModule('toolbar') as { container?: HTMLElement } | undefined;
    const pulsante = toolbar?.container?.querySelector<HTMLButtonElement>('.ql-uploadVideo');
    if (!pulsante) {
      return;
    }
    const stato = this.videoInCorso();
    pulsante.textContent = this.avanzamentoVideo(stato);
    pulsante.disabled = stato !== null;
    pulsante.setAttribute('aria-label', pulsante.textContent);
    pulsante.setAttribute('aria-busy', String(stato !== null));
    pulsante.title = 'Carica un video dal dispositivo nel punto del cursore';
  }

  private aggiornaStatoVideo(stato: StatoCaricamento | null): void {
    this.videoInCorso.set(stato);
    this.aggiornaPulsanteVideo();
  }

  /**
   * Carica un video e lo inserisce nel punto in cui sta il cursore.
   *
   * La posizione si legge prima di aprire il selettore di file: aprendo la
   * finestra di sistema l'editor perde il fuoco, e un insertEmbed fatto dopo
   * finirebbe in fondo all'articolo invece che dove lavora il redattore.
   */
  caricaVideo(): void {
    // Il comando non compare a chi non e' `owners`, ma il metodo e' pubblico e
    // richiamabile: meglio che si rifiuti anche da solo.
    if (!this.puoCaricareVideo()) {
      return;
    }
    if (this.videoInCorso() !== null) {
      return;
    }
    const selezione = this.quillEditor.quillEditor.getSelection(true);
    const indice = selezione?.index ?? this.quillEditor.quillEditor.getLength();

    const input = this.renderer.createElement('input');
    this.renderer.setAttribute(input, 'type', 'file');
    this.renderer.setAttribute(input, 'accept', 'video/*');
    this.renderer.setStyle(input, 'display', 'none');
    this.renderer.appendChild(document.body, input);

    input.addEventListener('change', (evento: any) => {
      const file: File = evento.target.files?.[0];
      this.renderer.removeChild(document.body, input);
      if (!file) {
        return;
      }

      this.aggiornaStatoVideo({ fase: 'carico', percentuale: 0 });
      this.videoService.carica(file).subscribe({
        next: (stato) => {
          this.aggiornaStatoVideo(stato);
          if (stato.fase === 'pronto') {
            this.quillEditor.quillEditor.insertEmbed(indice, 'embed-video-file', stato.video);
            this.quillEditor.quillEditor.insertText(indice + 1, '\n');
            this.editItemForm.controls['testo'].setValue(testoSenzaAiutiEditor(this.quillEditor.quillEditor.root.innerHTML));
            this.preparaVideoTrascinabili();
            this.utilsService.notificationsService.showNotification('', 'Video inserito', 'success');
          }
        },
        error: (err) => {
          console.error('Errore caricamento video:', err);
          this.aggiornaStatoVideo(null);
          this.utilsService.notificationsService.showNotification(
            '',
            err?.error?.messaggio || 'Caricamento non riuscito, riprova.',
            'error'
          );
        },
        complete: () => this.aggiornaStatoVideo(null),
      });
    });

    input.click();
  }

  showPDFPopup(): void {
    const articleId = this.editItemForm.controls['id'].value;
    if (!articleId) {
      this.utilsService.notificationsService.showNotification('', 'Salva prima l\'articolo, poi inserisci il PDF', 'error');
      return;
    }

    const savedSelection = this.quillEditor.quillEditor.getSelection(true);
    // Creazione dinamica dell'input file
    const fileInput = this.renderer.createElement('input');
    this.renderer.setAttribute(fileInput, 'type', 'file');
    this.renderer.setAttribute(fileInput, 'accept', 'application/pdf');
    this.renderer.setStyle(fileInput, 'display', 'none');

    // Aggiunge l'input file al DOM
    this.renderer.appendChild(document.body, fileInput);

    // Listener per la selezione del file
    fileInput.addEventListener('change', (event: any) => {
      const file: File = event.target.files[0];
      const isPdf = file && (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'));
      if (isPdf) {
        this.uploadService.uploadPdf(file, articleId).subscribe({
          next: (uploadedPdf) => {
            const selectedText = savedSelection?.length
              ? this.quillEditor.quillEditor.getText(savedSelection.index, savedSelection.length).trim()
              : '';
            const nodeData = { href: uploadedPdf.href, name: selectedText || file.name };
            const index = Math.min(savedSelection?.index ?? this.quillEditor.quillEditor.getLength(), this.quillEditor.quillEditor.getLength());
            this.quillEditor.quillEditor.insertEmbed(index, 'embed-pdf', nodeData);
            this.quillEditor.quillEditor.insertText(index + 1, '\n');
            this.editItemForm.controls['testo'].setValue(testoSenzaAiutiEditor(this.quillEditor.quillEditor.root.innerHTML));
            this.utilsService.notificationsService.showNotification('', 'PDF caricato con successo', 'success');
          },
          error: (err) => {
            this.utilsService.defaultHttpErrorHandler(err);
          },
        });
      } else {
        this.utilsService.notificationsService.showNotification('', 'Si possono caricare solo file PDF', 'error');
      }
      // Rimuove l'input file dal DOM dopo l'uso
      this.renderer.removeChild(document.body, fileInput);
    });

    // Simula il click sull'input file per aprire il popup
    fileInput.click();
  }

  // file methods
  imageName = signal('');
  fileSize = signal(0);
  uploadProgress = signal(0);
  imagePreview = signal('');

  /**
   * Dimensioni minime richieste da Google per l'immagine di copertina.
   *
   * Non sono una preferenza nostra: per comparire in Google Discover con
   * l'anteprima a immagine grande Google richiede larghezza di almeno 1200 px,
   * piu di 300.000 pixel totali e proporzione 16:9. Le copertine caricate finora
   * sono 600x350, cioe 210.000 pixel: meta della larghezza minima e il 70% dei
   * pixel richiesti. Il sito chiede correttamente il trattamento a immagine
   * grande (max-image-preview:large e servito nell'HTML) ma consegna
   * un'immagine che non lo consente, e in tre mesi Discover ha portato zero
   * impressioni.
   *
   * L'avviso non blocca il caricamento: la scelta resta alla redazione.
   */
  readonly LARGHEZZA_MINIMA = 1200;
  readonly ALTEZZA_MINIMA = 675;
  readonly PIXEL_MINIMI = 300000;

  /** Esito del controllo sulle dimensioni: null finche non si carica nulla. */
  avvisoImmagine = signal<{
    larghezza: number;
    altezza: number;
    pixel: number;
    larghezzaInsufficiente: boolean;
    pixelInsufficienti: boolean;
    proporzioneErrata: boolean;
  } | null>(null);
  immagineConforme = signal(false);
  @ViewChild('fileInput') fileInput: ElementRef | undefined;
  selectedFile: File | null = null;
  uploadSuccess: boolean = false;
  uploadError: boolean = false;

  /**
   * Legge le dimensioni reali dell'immagine appena scelta e valuta se rispetta i
   * requisiti di Google Discover. Non blocca nulla: alimenta solo l'avviso.
   */
  private controllaDimensioni(base64Img: string): void {
    const img = new Image();
    img.onload = () => {
      const larghezza = img.naturalWidth;
      const altezza = img.naturalHeight;
      const pixel = larghezza * altezza;
      const proporzione = altezza > 0 ? larghezza / altezza : 0;

      const larghezzaInsufficiente = larghezza < this.LARGHEZZA_MINIMA;
      const pixelInsufficienti = pixel <= this.PIXEL_MINIMI;
      // 16:9 e 1,778. Si tollera uno scarto del 10%, cosi un 1200x630 o un
      // 1200x720 non fanno scattare l'avviso per un dettaglio.
      const proporzioneErrata = Math.abs(proporzione - 16 / 9) > 0.18;

      if (larghezzaInsufficiente || pixelInsufficienti || proporzioneErrata) {
        this.avvisoImmagine.set({
          larghezza,
          altezza,
          pixel,
          larghezzaInsufficiente,
          pixelInsufficienti,
          proporzioneErrata,
        });
        this.immagineConforme.set(false);
      } else {
        this.avvisoImmagine.set(null);
        this.immagineConforme.set(true);
      }
    };
    img.onerror = () => {
      this.avvisoImmagine.set(null);
      this.immagineConforme.set(false);
    };
    img.src = base64Img;
  }

  // Handler for file input change
  onFileChange(event: any): void {
    const file = event.target.files[0] as File | null;
    this.uploadFile(file);
  }

  // Handler for file drop
  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files[0] as File | null;
    this.uploadFile(file);
  }

  // Prevent default dragover behavior
  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  /**
   * Carica la copertina.
   *
   * L'immagine passa prima da preparaCopertina: le PNG opache da 2-4 MB che la
   * redazione usa quasi sempre partono come JPEG da qualche centinaio di KB, e
   * su una linea lenta sono i secondi che si aspettavano.
   */
  async uploadFile(file: File | null): Promise<void> {
    if (!file || !file.type.startsWith('image/')) {
      this.uploadSuccess = false;
      this.uploadError = true;
      this.snackBar.open('Puoi inserire solo immagini', 'Chiudi', {
        duration: 3000,
        panelClass: 'error',
      });
      return;
    }

    this.selectedFile = file;
    this.uploadSuccess = true;
    this.uploadError = false;
    this.imageName.set(file.name); // Set image name

    const pronta = await preparaCopertina(file);
    this.fileSize.set(Math.round(pronta.byte / 1024)); // Set file size in KB
    this.imagePreview.set(pronta.dataUrl); // Set image preview URL
    this.controllaDimensioni(pronta.dataUrl);

    const diskName = this.utilsService.generateRandomString(22);
    const fileSystem = new FileSys({
      attachment_id: this.editItemForm.controls['id'].value,
      disk_name: diskName,
      file_name: pronta.nome,
      file_size: pronta.byte,
      content_type: pronta.tipo,
      attachment_type: `Infooggi\\Articoli\\Models\\Article`,
      field: 'imgUp',
      base64Img: pronta.dataUrl,
      file_path: this.utilsService.generateFilePath(diskName),
    });
    this.uploadService.uploadFile(fileSystem).subscribe(
      (res) => {
        if (this.editItem) {
          // Il server rimanda indietro anche l'immagine in base64: nel form non
          // serve, e da li' viaggerebbe con ogni salvataggio dell'articolo.
          const { base64Img, ...salvato } = res;
          this.editItem.fileSystem = new FileSys(salvato);
          this.editItemForm.controls['fileSystem'].setValue(salvato);
          this.utilsService.notificationsService.showNotification('', 'Immagine caricata con successo', 'success');
        }
      },
      (err) => {
        this.snackBar.open("Errore durante il caricamento dell'immagine", 'Chiudi', {
          duration: 3000,
          panelClass: 'error',
        });
        this.uploadSuccess = false;
        this.uploadError = true;
      }
    );
  }

  // Method to remove the uploaded image
  removeImage(): void {
    this.selectedFile = null;
    this.imageName.set('');
    this.fileSize.set(0);
    this.imagePreview.set('');
    this.uploadSuccess = false;
    this.uploadError = false;
    this.uploadProgress.set(0);
    this.avvisoImmagine.set(null);
    this.immagineConforme.set(false);
  }

  // AI
  async generaTesto() {
    let value = await this.utilsService.prompt('Inserisci informazioni a corredo per migliorare la generazione del testo', "riscrivi questo testo con un taglio giornalistico...oppure...basandoti sul titolo, scrivi un testo con un taglio giornalistico di 2000 caratteri, ecc");
    if (value != null) {
      let prompt = "";
      if (this.editItemForm.controls['testo'].value != null && this.editItemForm.controls['testo'].value.length > 0) {
        prompt += "Dato questo testo: \n\n" + this.editItemForm.controls['testo'].value + "\n\n, modificalo seguendo le seguenti linee guida: ";
      } else {
        prompt += "Dato il titolo dell'articolo giornalistico: \n\n" + this.editItemForm.controls['titolo'].value + "\n\n, scrivi un testo seguendo le seguenti linee guida: ";
      }
      prompt += value;
      prompt += "\nIl testo prodotto deve essere in formato HTML, non rispondere con nient'altro che il testo richiesto modificato.\nNon inserire alcun titolo nel testo prodotto.\nNon inserire la parola HTML o simboli strani nel testo, puoi utilizzare solo i tag <b>, <br>, <i>.";
      prompt += "\nRispondi con un JSON contenente la tua risposta. Il JSON deve avere una chiave 'result' che contiene il testo formattato in HTML.";
      await this.scriviInStreaming(prompt, this.generazioneInCorso);
    }
  }

  /** Etichetta del pulsante mentre il modello lavora. */
  avanzamento(stato: StatoGenerazione | null): string {
    return etichettaAvanzamento(stato);
  }

  /**
   * Fa scrivere il modello dentro l'editor, un pezzo per volta.
   *
   * Il testo di partenza viene messo da parte prima di svuotare l'editor: se la
   * connessione cade a metà (o la risposta non passa i controlli del backend)
   * l'articolo torna come era, invece di restare troncato dove il modello si e'
   * interrotto.
   */
  private async scriviInStreaming(prompt: string, inCorso: WritableSignal<StatoGenerazione | null>): Promise<void> {
    const controlloTesto = this.editItemForm.controls['testo'];
    const testoDiPartenza: string | null = controlloTesto.value;
    inCorso.set({ caratteriScritti: 0, sta: 'ragionando' });

    let parziale = '';
    let ultimoDisegno = 0;
    try {
      await new Promise<void>((risolvi, rifiuta) => {
        // Nessun reasoning_effort: la riscrittura ha bisogno di ragionare, e qui
        // si sta cambiando il modo di consegnare la risposta, non la qualita'.
        this.streamAperto = this.aiService.callAIStream(prompt).subscribe({
          next: (evento: EventoAI) => {
            if (evento.tipo === 'pezzo') {
              if (parziale.length === 0) {
                // Primo pezzo: da qui l'editor mostra il testo nuovo.
                controlloTesto.setValue('');
              }
              parziale += evento.testo;
              // Il modello manda migliaia di frammenti minuscoli: riscrivere
              // Quill a ogni frammento inchioda il browser, quindi l'editor si
              // aggiorna a scatti di un sesto di secondo.
              const adesso = Date.now();
              if (adesso - ultimoDisegno > 150) {
                ultimoDisegno = adesso;
                controlloTesto.setValue(parziale);
              }
              inCorso.set({ caratteriScritti: parziale.length, sta: 'scrivendo' });
            } else if (evento.tipo === 'fine') {
              controlloTesto.setValue(evento.result);
            }
          },
          error: (err) => rifiuta(err),
          complete: () => risolvi(),
        });
      });
      this.utilsService.notificationsService.showNotification('', 'Testo aggiornato', 'success');
    } catch (err: any) {
      console.error('Errore generazione testo:', err);
      controlloTesto.setValue(testoDiPartenza);
      this.utilsService.notificationsService.showNotification('', messaggioErroreFormattazione(err), 'error');
    } finally {
      this.streamAperto?.unsubscribe();
      this.streamAperto = null;
      inCorso.set(null);
    }
  }
  /**
   * Rimette in forma un testo arrivato dall'esterno senza formattazione: gli
   * articoli inviati dal modulo pubblico arrivano quasi sempre come un unico
   * blocco. Il modello puo' solo aggiungere tag, e la risposta viene applicata
   * soltanto se contiene esattamente le stesse parole del testo di partenza.
   */
  async miglioraFormattazione() {
    const testoOriginale: string | null = this.editItemForm.controls['testo'].value;
    if (testoOriginale == null || testoOriginale.trim().length === 0 || this.formattazioneInCorso()) {
      return;
    }

    // Il prompt precedente era tutto divieti e nessun criterio di taglio: il
    // modello se la cavava avvolgendo l'intero articolo in un solo <p>, e la
    // redazione vedeva il testo tornare identico. Le regole su dove aprire un
    // paragrafo sono la parte che fa il lavoro.
    const prompt = `Sei un redattore. Ricevi il testo di un articolo arrivato come un blocco unico e lo devi restituire suddiviso in paragrafi HTML.

Come dividere il testo:
- ogni paragrafo va racchiuso in <p>...</p>
- apri un paragrafo nuovo a ogni cambio di argomento, di norma ogni 2-4 frasi
- apri un paragrafo nuovo prima di una dichiarazione o di una citazione, e un altro quando la citazione finisce e il discorso torna alla cronaca
- apri un paragrafo nuovo quando cambia il tema trattato
- un testo di piu di cinque frasi non puo' restare in un unico paragrafo: i paragrafi devono essere almeno tre

Grassetto, con parsimonia:
- metti in <b>...</b> solo le poche espressioni che il lettore deve cogliere a colpo d'occhio: un nome proprio che conta, una cifra o una data decisiva, il concetto attorno a cui ruota il paragrafo
- al massimo una espressione in grassetto per paragrafo, e in diversi paragrafi nessuna
- il grassetto va su una o poche parole, mai su una frase intera e mai su una citazione intera
- se un paragrafo non ha niente di davvero saliente, lascialo senza grassetto

Intertitoli gia' presenti nel testo:
- capita che il testo contenga righe brevi che annunciano il paragrafo dopo, spesso senza punto finale e attaccate alla frase precedente
- vanno conservate parola per parola: mettile in un paragrafo per conto loro, tutto in <b>
- non cancellarle mai, e non riscriverle

Vincolo sul contenuto: le parole devono restare esattamente quelle ricevute. Non aggiungere, non togliere, non riformulare e non correggere nulla, nemmeno la punteggiatura o le maiuscole. Il tuo intervento si limita a inserire i tag. Se una frase ti sembra un titolo, un residuo o un doppione, lasciala comunque dov'e': non sei tu a decidere cosa taglia la redazione.

Altri vincoli:
- usa solo i tag <p>, <br>, <b>, <i>, <a>, e conserva quelli gia presenti nel testo
- non aggiungere titoli tuoi, non trasformare il testo in un elenco, non commentare

Testo da formattare:
<<<
${testoOriginale}
>>>

Rispondi con un JSON con una sola chiave "result" che contiene il testo formattato in HTML.`;

    this.formattazioneInCorso.set(true);
    this.tentativoFormattazione.set(1);
    try {
      const formattato = await formattaConTentativi(
        testoOriginale,
        () => firstValueFrom(this.aiService.callAI(prompt, 'none')),
        { onTentativo: (n) => this.tentativoFormattazione.set(n) }
      );
      this.editItemForm.controls['testo'].setValue(formattato);
      this.utilsService.notificationsService.showNotification('', 'Formattazione applicata', 'success');
    } catch (err: any) {
      console.error('Errore formattazione articolo:', err);
      const messaggio =
        err instanceof ErroreFormattazione
          ? err.motivo === 'alterato'
            ? 'La formattazione ha alterato il testo in tutti i tentativi: non e\' stata applicata.'
            : 'Il modello non ha restituito il testo formattato. Riprova.'
          : messaggioErroreFormattazione(err);
      this.utilsService.notificationsService.showNotification('', messaggio, 'error');
    } finally {
      this.formattazioneInCorso.set(false);
      this.tentativoFormattazione.set(1);
    }
  }

  /**
   * Le keywords dell'articolo.
   *
   * `reasoning_effort: 'none'` non e' un'ottimizzazione: e' la differenza fra
   * rispondere e non rispondere. Davanti al pannello c'e' Cloudflare, che
   * chiude con un 524 ogni richiesta che non ha risposto entro **100
   * secondi**, e senza quel parametro il modello ragiona a lungo su un compito
   * che di ragionamento non ne ha bisogno — estrarre cinque parole da un
   * testo. Misurato sull'articolo 155340, 3.303 caratteri: col ragionamento
   * predefinito la chiamata non e' rientrata nemmeno in dieci minuti, con
   * `none` pochi secondi.
   *
   * Prima non c'era ne' un segnale di avanzamento ne' un `catch`: la richiesta
   * restava appesa, poi Cloudflare la chiudeva, e chi aveva premuto il
   * pulsante non vedeva succedere niente — ne' un risultato ne' un errore.
   */
  async generaKeywords(): Promise<void> {
    const testo = this.editItemForm.controls['testo'].value;
    if (!testo) return;
    if (this.keywordsInCorso()) return;

    const prompt =
      'Dato questo testo: \n\n' + testo +
      "\n\n, genera 5 keywords efficaci per la SEO.\nOgni keyword deve essere separata da uno spazio non deve contenere caratteri speciali.\nLe keywords composte da più parole devono essere tutte attaccate e separate da un trattino, es: valle-daosta o francesco-totti.\nNon concludere il tuo messaggio con un punto o nessun altro carattere.\nNella tua risposta non scrivere nient'altro al di fuori delle keywords." +
      "\nRispondi con un JSON contenente le keywords generate. Il JSON deve avere una chiave 'result' che contiene la stringa con le keywords separate da uno spazio. ATTENZIONE: è importante che le keywords siano separate da uno spazio e non contengano caratteri speciali.";

    this.keywordsInCorso.set(true);
    try {
      const res: any = await firstValueFrom(this.aiService.callAI(prompt, 'none'));
      const keywords = String(res?.result ?? '').trim();
      if (!keywords) throw new Error('risposta senza keywords');
      this.editItemForm.controls['keywords'].setValue(keywords);
      this.utilsService.notificationsService.showNotification('', 'Keywords generate', 'success');
    } catch (err: any) {
      console.error('Errore generazione keywords:', err);
      // Il 524 di Cloudflare arriva come corpo HTML, quindi senza `messaggio`:
      // qualunque `err.error.messaggio` cadrebbe sul testo generico. Meglio
      // dire cosa fare invece di dire che qualcosa e' andato storto.
      const messaggio =
        err?.status === 504 || err?.status === 524 || err?.status === 0
          ? 'Il modello ci ha messo troppo e la richiesta e\' stata chiusa. Riprova fra poco.'
          : 'Generazione delle keywords non riuscita. Riprova.';
      this.utilsService.notificationsService.showNotification('', messaggio, 'error');
    } finally {
      this.keywordsInCorso.set(false);
    }
  }

  onContentChanged(event: any) {
    this.testo = event.html;
  }

  submitEditorChat() {
    let text = `
      Dato il testo che ti fornirò riguardante un articolo giornalistico, apporta le seguenti correttive che ti fornirò in seguito.
      Ecco il testo iniziale:
      <testo>${this.editItemForm.controls['testo'].value}</testo>
      Ecco le migliorie che devi apportare:
      <migliorie>${this.editItemForm.controls['chat_con_editor'].value}.</migliorie>
      Rispondi solo ed esclusivamente con il testo che hai modificato, non inserire nient'altro.`;
      text += "\nRispondi con un JSON contenente la tua risposta. Il JSON deve avere una chiave 'result' che contiene la risposta. ATTENZIONE, la risposta deve sempre essere in html";

    this.scriviInStreaming(text, this.chatInCorso);
  }
}
